import React from 'react'
import { router } from '@inertiajs/react'
import dayjs from 'dayjs'

import { Slot } from '@hooks/useSlots'
import { formatDate } from '@utils/formatDate'
import { Button } from '@atoms'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@atoms/Tooltip'
import InfoCard from '@molecules/InfoCard'
import { CopiableValue } from '@molecules/CopiableValue'
import type { Group } from '@frontend/graphql/types.generated'
import { t } from '@utils/I18n'
import { SupporterTagsList } from '@molecules/SupporterTagsList'
import type { SupporterShowType } from '../../types/supporterTypes'
import { useDialog } from '@components/DialogProvider'

type SupporterType = SupporterShowType['group']['supporters']['edges'][0]

type SupporterShowCardType = {
  group: Pick<Group, 'id' | 'name'>
  supporter: SupporterType['node']
  membership: SupporterType['membership']
  unsubscriber: SupporterType['unsubscriber']
  canRemoveMembers: boolean
  requestMemberRemovals: boolean
  canSendIndividualSms?: boolean
  canMakePhoneCalls?: boolean
  demoSite?: boolean
}

function lastContactedByline(dateString: string | null | undefined): string {
  if (!dateString) return t('supporter_show.never_contacted_byline')

  const date = dayjs(dateString)
  if (!date.isValid()) return t('supporter_show.never_contacted_byline')

  const relative = date.fromNow()
  const absolute = date.format('D MMM YYYY')
  return t('supporter_show.last_contacted_byline', { time_ago: `${relative} (${absolute})` })
}

export function SupporterShowCard({
  group,
  supporter,
  membership,
  unsubscriber: contactPreferences,
  canRemoveMembers,
  requestMemberRemovals,
  canSendIndividualSms = true,
  canMakePhoneCalls = true,
  demoSite = false,
}: SupporterShowCardType) {
  const { open: openDialog } = useDialog()
  const { id, firstName, lastName, fullName, email, primaryMobile, lastContactedAt, engagementLevel, note, tags, createdAt, externalCreatedAt } =
    supporter
  const { stopReceivingEmails, stopReceivingSms, stopReceivingCalls } = contactPreferences ?? {}
  const joinedGroup = formatDate(membership.createdAt)
  const joinedOrganisation = formatDate(externalCreatedAt ?? createdAt)
  const supporterName = firstName ?? fullName

  const translationVariables = { name: supporterName, groupName: group.name }
  const removeFromGroupLabel = canRemoveMembers
    ? t('supporter_show.remove_supporter.button', translationVariables)
    : t('supporter_show.request_supporter_removal.button', translationVariables)
  const removeFromGroupAriaLabel = canRemoveMembers
    ? t('supporter_show.remove_supporter.aria_label', translationVariables)
    : t('supporter_show.request_supporter_removal.aria_label', translationVariables)
  const removeFromGroupConfirmation = canRemoveMembers
    ? t('supporter_show.remove_supporter.confirmation', translationVariables)
    : t('supporter_show.request_supporter_removal.confirmation', translationVariables)

  const removeGroupMember = () => {
    if (window.confirm(removeFromGroupConfirmation)) {
      router.post(`/groups/${group.id}/supporters/${id}/remove`, {}, { preserveState: false })
    }
  }

  const tagNodes = tags?.nodes ?? []

  const detailRows = [
    { label: t('supporter_show.joined_group'), content: joinedGroup, testId: 'joined-group' },
    { label: t('supporter_show.joined_organisation'), content: joinedOrganisation, testId: 'joined-organisation' },
    ...(engagementLevel
      ? [
          {
            label: t('supporter_show.engagement_level'),
            content: engagementLevel,
            testId: 'engagement-level',
          },
        ]
      : []),
    {
      label: t('supporter_show.background_note.title'),
      content: note ?? t('supporter_show.background_note.empty_state'),
      testId: 'background-note',
      isNote: true,
    },
  ]

  return (
    <InfoCard data-testid={`supporter-card-${id}`}>
      <Slot name="heading">
        <div className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-3 tw:gap-y-1 tw:min-w-0">
          <h2 className="tw:text-lg tw:font-semibold tw:text-gray-900 tw:m-0">{fullName}</h2>
          <span className="tw:inline-flex tw:flex-wrap tw:gap-x-3 tw:gap-y-1 tw:text-sm tw:text-gray-500">
            {email && (
              <CopiableValue icon="mail-01" value={email} copyLabel={t('components.supporter_details.contact_info.copy_email')} testId="email" />
            )}
            {primaryMobile && (
              <CopiableValue
                icon="phone-call-01"
                value={primaryMobile}
                copyLabel={t('components.supporter_details.contact_info.copy_phone')}
                testId="phone"
              />
            )}
          </span>
        </div>
        <p className="tw:text-sm tw:text-gray-500 tw:italic tw:m-0" data-testid="last-contacted">
          {lastContactedByline(lastContactedAt)}
        </p>
      </Slot>

      <Slot name="main">
        <div className="tw:flex tw:flex-col tw:gap-3">
          {tagNodes.length > 0 && (
            <div className="tw:-mt-2">
              <SupporterTagsList tags={tagNodes} maxVisibleTags={3} />
            </div>
          )}

          <dl className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-[max-content_1fr] tw:gap-x-3 tw:gap-y-1 tw:sm:gap-y-3 tw:items-center">
            {detailRows.map(({ label, content, testId, isNote }, index, array) => {
              const isLast = index === array.length - 1
              return (
                <React.Fragment key={testId}>
                  <dt
                    className={`tw:text-xs tw:text-gray-blue-500 tw:uppercase tw:font-bold tw:flex ${isNote ? 'tw:sm:items-start' : 'tw:items-center'}`}
                  >
                    {label}
                  </dt>
                  <dd
                    className={`tw:text-sm tw:text-gray-900 tw:m-0 tw:flex ${isNote ? 'tw:sm:items-start' : 'tw:items-center'} ${isLast ? 'tw:mb-0' : 'tw:mb-3'} tw:sm:mb-0`}
                    data-testid={testId}
                  >
                    {content}
                  </dd>
                </React.Fragment>
              )
            })}
          </dl>
        </div>
      </Slot>

      <Slot name="actions">
        <Slot name="primary">
          <Button
            size="sm"
            rank="primary"
            ariaLabel={t('components.supporter_card.actions.log_contact_aria', {
              name: firstName,
            })}
            onClick={() => {
              openDialog({
                type: 'logContact',
                dialogData: { id, firstName, lastName, groupId: group.id },
              })
            }}
          >
            {t('components.supporter_card.actions.log_contact', { name: supporterName })}
          </Button>
          {demoSite ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="mail-01"
                    disabled
                    ariaLabel={t('components.supporter_card.actions.send_email_aria', { name: firstName })}
                  >
                    {t('components.supporter_card.actions.send_email', { name: firstName })}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('shared.demo_site.sending_disabled')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              size="sm"
              rank="secondary"
              leadingIcon="mail-01"
              disabled={!membership.emailable}
              ariaLabel={t('components.supporter_card.actions.send_email_aria', { name: firstName })}
              onClick={() => {
                openDialog({
                  type: 'sendEmail',
                  dialogData: { id, firstName, lastName, groupId: group.id },
                })
              }}
            >
              {t('components.supporter_card.actions.send_email', { name: firstName })}
            </Button>
          )}
          {canSendIndividualSms && !demoSite ? (
            <Button
              size="sm"
              rank="secondary"
              leadingIcon="message-chat-square"
              disabled={!membership.textable}
              ariaLabel={t('components.supporter_card.actions.send_sms_aria', { name: firstName })}
              onClick={() => {
                openDialog({
                  type: 'sendSms',
                  dialogData: { id, firstName, lastName, groupId: group.id },
                })
              }}
            >
              {t('components.supporter_card.actions.send_sms', { name: firstName })}
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="message-chat-square"
                    disabled
                    ariaLabel={t('components.supporter_card.actions.send_sms_aria', { name: firstName })}
                  >
                    {t('components.supporter_card.actions.send_sms', { name: firstName })}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {demoSite ? t('shared.demo_site.sending_disabled') : t('components.supporter_card.actions.send_sms_disabled')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {canMakePhoneCalls && !demoSite ? (
            <Button
              size="sm"
              rank="secondary"
              leadingIcon="phone-call-01"
              disabled={!membership?.callable}
              ariaLabel={t('components.supporter_card.actions.call_aria', { name: firstName })}
              onClick={() => {
                openDialog({
                  type: 'initiateCall',
                  dialogData: { id, firstName, lastName, groupId: group.id },
                })
              }}
            >
              {t('components.supporter_card.actions.call', { name: firstName })}
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="phone-call-01"
                    disabled
                    ariaLabel={t('components.supporter_card.actions.call_aria', { name: firstName })}
                  >
                    {t('components.supporter_card.actions.call', { name: firstName })}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {demoSite ? t('shared.demo_site.calls_disabled') : t('components.supporter_card.actions.call_disabled')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Slot>

        <Slot name="secondary">
          <Button
            size="sm"
            rank="link"
            leadingIcon="user-x"
            ariaLabel={t('components.supporter_card.actions.update_contact_preferences_aria', {
              name: firstName,
            })}
            onClick={() => {
              openDialog({
                type: 'updateContactPreferences',
                dialogData: {
                  supporter: { id, firstName, lastName },
                  currentContactPreferences: {
                    stopReceivingEmails,
                    stopReceivingSms,
                    stopReceivingCalls,
                  },
                  groupId: group.id,
                },
              })
            }}
          >
            {t('components.supporter_card.actions.update_contact_preferences')}
          </Button>
          {(canRemoveMembers || requestMemberRemovals) && (
            <Button
              type="button"
              size="sm"
              rank="secondary"
              color="danger"
              leadingIcon="trash-01"
              ariaLabel={removeFromGroupAriaLabel}
              onClick={removeGroupMember}
            >
              {removeFromGroupLabel}
            </Button>
          )}
        </Slot>
      </Slot>
    </InfoCard>
  )
}
